import {Directive, ElementRef, forwardRef, Input, OnChanges} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash-es';
import {Observable} from 'rxjs';
import {skip, tap} from 'rxjs/operators';
import {dependOn} from '../../common/rxjs/depend-on';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {normalizeTranslationArgs} from '../translate/translation-args';
import {TooltipDirective} from './tooltip.directive';
import {TooltipService} from './tooltip.service';

@Directive({
    selector: '[tooltipL10n]',
    providers: [
        TooltipService,
        {
            provide: TooltipDirective,
            useExisting: forwardRef(() => TooltipL10nDirective)
        }
    ],
    exportAs: 'tooltip',
    standalone: false
})
export class TooltipL10nDirective extends TooltipDirective implements OnChanges {

    @Input('tooltipL10n')
    public override tooltip: string;

    @Input('tooltipL10nArgs')
    public tooltipArgs: any;

    @Input('tooltipL10nFallback')
    public tooltipFallback?: string;

    protected override dynamicTooltip: string;

    constructor(
        elementRef: ElementRef,
        tooltipService: TooltipService,
        domSanitizer: DomSanitizer,
        private translationService: L10nTranslationService
    ) {
        super(elementRef, tooltipService, domSanitizer);
    }

    public override ngOnChanges(changes: TypedSimpleChanges<TooltipL10nDirective>): void {
        super.ngOnChanges(changes);

        if (changes.tooltipArgs) {
            this.updateTooltipContent$.next();
        }
    }

    protected override getTooltipContent(): string {
        const tooltipValue = this.isDynamicTooltipMode
            ? this.dynamicTooltip
            : this.tooltip;

        const args = normalizeTranslationArgs(this.tooltipArgs);

        return _.isString(tooltipValue)
            ? !_.isString(this.tooltipFallback) || this.translationService.has(tooltipValue)
                ? this.translationService.translate(tooltipValue, args)
                : this.tooltipFallback
            : String(tooltipValue || '');
    }

    protected override createTooltipContentStream(): Observable<string> {
        return super.createTooltipContentStream().pipe(
            dependOn(this.listenToLanguageChange())
        );
    }

    private listenToLanguageChange(): Observable<any> {
        return this.translationService.onChange().pipe(
            skip(1), // Skip the initial language value.
            tap(() => this.updateTooltipContent$.next())
        );
    }

}
